import {
  ANIMAL_NAMES, BIOMES, crowdedBy, findOpenSpot, handPlacedSpots, localDay, seedCount, SEED_KINDS, setSuggestedSpots, stageRank, suggestedSpots,
  type AnimalKind, type Biome, type GameState, type Plant, type Planting, type PlantingProblem,
} from '@thicket/core';
import { html, nothing } from 'lit-html';
import { ref } from 'lit-html/directives/ref.js';
import { art, icon } from '../garden/art/index.js';
import { labReady, scenery, type Piece } from '../garden/art/lab/index.js';
import { isPlantable, tileAt } from '../garden/maps.js';
import { drawPlantingAids, drawScene, glowSpots, H, hitTest, plantProblem, spotFor, W, type PlantingAids } from '../garden/scene.js';
import { app, BIOME_NAMES, SEED_NAMES, toast, update } from './app.js';
import { treeName } from './names.js';
import { devPanel } from './dev.js';
import { artState, startLab } from './labLoad.js';

const ANIMAL_NOTES: Record<AnimalKind, string> = {
  songbird: 'Sings from a small tree or bigger', squirrel: 'Climbs a medium tree or bigger', owl: 'Roosts in a large tree',
  bumblebee: 'Buzzes around the bed and cart', toad: 'Sits by the bed and cart', butterfly: 'Flutters around the bed and cart',
};

let canvas: HTMLCanvasElement | null = null;
let hover: { c: number; r: number } | null = null;
let bedPick: string | null = null;
let tick = 0;
let looping = false;
const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const hashOf = (s: string) => [...s].reduce((h, ch) => Math.imul(h ^ ch.charCodeAt(0), 16777619) >>> 0, 2166136261);

/** 'close' fills the screen with the scene (drag to look around); 'fit' shows all of it. */
type Zoom = 'close' | 'fit';
let zoom: Zoom = (() => {
  try {
    return localStorage.getItem('thicket.zoom') === 'fit' ? 'fit' : 'close';
  } catch {
    return 'close';
  }
})();
let centred = false;
/** Pinch or trackpad zoom on top of the close-up: 1 fills the screen, up to 4× closer. */
let extra = 1;
const MAX_EXTRA = 4;

function setZoom(z: Zoom) {
  zoom = z;
  extra = 1;
  centred = false;
  try {
    localStorage.setItem('thicket.zoom', z);
  } catch {
    // Only a preference.
  }
  update();
}

const currentBiome = (s: GameState): Biome => app.biome ?? s.profile.starterBiome;
const taskText = (s: GameState, taskId: string | null) => s.tasks.find((t) => t.id === taskId)?.text ?? '';

/** What is being planted right now, for spacing: a tree needs room for its mega size. */
function plantingWhat(s: GameState): Planting | null {
  const pl = app.planting;
  if (!pl) return null;
  if (pl.kind === 'seed') return { kind: 'flower', pace: 'trivial' };
  const p = s.plants.find((x) => x.id === pl.plantId);
  return p ? { kind: p.kind, pace: p.pace, id: p.id } : null;
}

/** A tap that couldn't plant: its red marker stays up for a couple of seconds, for touch screens with no hover. */
let refused: { c: number; r: number; problem: PlantingProblem; until: number } | null = null;

function plantingAids(s: GameState, biome: Biome): PlantingAids | null {
  if (spotEdit.on) {
    return { glow: suggestedSpots(biome), at: hover ? { ...hover, problem: isPlantable(art.map(biome), hover.c, hover.r) ? null : 'ground' } : null, crowding: [] };
  }
  const what = plantingWhat(s);
  if (!what) return null;
  if (refused && refused.until < tick) refused = null;
  const at = hover ? { ...hover, problem: plantProblem(s, biome, hover.c, hover.r, what) } : refused;
  const crowding = at?.problem === 'crowded' ? crowdedBy(s, biome, at.c, at.r, what).map((p) => ({ x: p.x!, y: p.y! })) : [];
  return { glow: glowSpots(s, biome, what), at, crowding };
}

function draw() {
  const s = app.store.view;
  if (!canvas || !s) return;
  const ctx = canvas.getContext('2d')!;
  // Until the Lab art arrives the scene stays clear, over the soft meadow behind it.
  if (art.name === 'lab' && !labReady()) return ctx.clearRect(0, 0, W, H);
  const biome = currentBiome(s);
  if (app.dev) useDevSpots();
  const aids = plantingAids(s, biome);
  const today = localDay(app.store.now(), s.profile);
  if (art.scene) {
    art.scene(ctx, s, biome, { today, tick, reduce });
    if (aids) drawPlantingAids(ctx, aids, { tick: reduce ? 0 : tick, reduce });
    if (editor.on && editor.sel) {
      // A dashed box around the selected piece, following it while it is dragged.
      const q = editor.sel;
      const live = pieceDrag ? scenery.pieces().find((p) => p.id === q.id) : null;
      const x = (live?.x ?? q.x) - q.ax, y = (live?.y ?? q.y) - q.ay;
      ctx.save();
      ctx.strokeStyle = '#fff2b8';
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.strokeRect(x - 1.5, y - 1.5, q.w + 3, q.h + 3);
      ctx.restore();
    }
  } else drawScene(ctx, s, biome, { aids, today, tick, reduce });
}

function loop() {
  if (!canvas || !canvas.isConnected || app.tab !== 'garden') {
    looping = false;
    return;
  }
  tick++;
  draw();
  setTimeout(() => requestAnimationFrame(loop), 90);
}

let drawerOpen = false;

/** Opens or closes the garden tools drawer (the tour uses this). */
export function setDrawer(open: boolean) {
  update(() => (drawerOpen = open));
}
let sized: ResizeObserver | null = null;
/** Set when a mouse drag pans the scene, so the click that ends it doesn't count as a tap. */
let dragged = false;

/** Scales the scene to the screen: filling it in close-up (pan to see the rest), or all of it in 'fit'. */
function layout() {
  if (!canvas) return;
  const frame = canvas.parentElement!;
  const fw = frame.clientWidth;
  const fh = frame.clientHeight;
  if (!fw || !fh) return;
  const k = zoom === 'close' ? Math.max(fw / W, fh / H) * extra : Math.min(fw / W, fh / H);
  canvas.style.width = `${Math.floor(W * k)}px`;
  canvas.style.height = `${Math.floor(H * k)}px`;
  if (zoom === 'close' && !centred) {
    frame.scrollLeft = (frame.scrollWidth - fw) / 2;
    frame.scrollTop = (frame.scrollHeight - fh) / 2;
    centred = true;
  }
}

function attach(el: Element | undefined) {
  canvas = (el as HTMLCanvasElement) ?? null;
  if (canvas && !looping) {
    looping = true;
    requestAnimationFrame(loop);
  }
  if (canvas) {
    sized?.disconnect();
    sized = new ResizeObserver(() => layout());
    sized.observe(canvas.parentElement!);
    layout();
  }
  draw();
}

/** Zooms the close-up by `factor` around a point on screen, so what's under it stays put. */
function zoomAt(frame: HTMLElement, factor: number, clientX: number, clientY: number) {
  if (!canvas) return;
  if (zoom === 'fit') {
    if (factor <= 1) return;
    zoom = 'close';
  }
  const next = Math.min(MAX_EXTRA, Math.max(1, extra * factor));
  if (next === extra) return;
  const r = frame.getBoundingClientRect();
  const before = canvas.getBoundingClientRect();
  // Where the point is on the scene, from 0 to 1.
  const u = (clientX - before.left) / before.width;
  const v = (clientY - before.top) / before.height;
  extra = next;
  centred = true;
  layout();
  const w = canvas.offsetWidth;
  const h = canvas.offsetHeight;
  frame.scrollLeft = u * w - (clientX - r.left);
  frame.scrollTop = v * h - (clientY - r.top);
}

/** Two fingers on the scene pinch-zoom it; one finger scrolls it as usual. */
const touches = new Map<number, { x: number; y: number }>();
let pinchGap = 0;
const gap = () => {
  const [a, b] = [...touches.values()];
  return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
};
function touchStart(e: PointerEvent) {
  if (e.pointerType !== 'touch') return;
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (touches.size === 2) pinchGap = gap();
}
function touchMove(e: PointerEvent) {
  if (e.pointerType !== 'touch' || !touches.has(e.pointerId)) return;
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (touches.size !== 2 || !pinchGap) return;
  const g = gap();
  const [a, b] = [...touches.values()];
  zoomAt(e.currentTarget as HTMLElement, g / pinchGap, (a!.x + b!.x) / 2, (a!.y + b!.y) / 2);
  pinchGap = g;
  dragged = true;
}
function touchEnd(e: PointerEvent) {
  touches.delete(e.pointerId);
  if (touches.size < 2) pinchGap = 0;
}
/** Ctrl + wheel (and trackpad pinch) zooms on a computer. */
function wheel(e: WheelEvent) {
  if (!e.ctrlKey) return;
  e.preventDefault();
  zoomAt(e.currentTarget as HTMLElement, Math.exp(-e.deltaY / 200), e.clientX, e.clientY);
}

/** Mouse drag pans the scene; touch screens scroll it natively. */
let pan: { x: number; y: number; sl: number; st: number } | null = null;
function panStart(e: PointerEvent) {
  if (e.pointerType !== 'mouse' || e.button !== 0 || zoom !== 'close') return;
  const f = e.currentTarget as HTMLElement;
  pan = { x: e.clientX, y: e.clientY, sl: f.scrollLeft, st: f.scrollTop };
  dragged = false;
}
function panMove(e: PointerEvent) {
  if (!pan) return;
  const f = e.currentTarget as HTMLElement;
  const dx = e.clientX - pan.x;
  const dy = e.clientY - pan.y;
  if (Math.abs(dx) + Math.abs(dy) > 4) dragged = true;
  f.scrollLeft = pan.sl - dx;
  f.scrollTop = pan.st - dy;
}
function panEnd() {
  pan = null;
}

function toLogical(e: PointerEvent | MouseEvent) {
  const r = canvas!.getBoundingClientRect();
  return { x: ((e.clientX - r.left) * W) / r.width, y: ((e.clientY - r.top) * H) / r.height };
}

function describe(s: GameState, p: Plant): string {
  const task = s.tasks.find((t) => t.id === p.taskId);
  if (p.kind === 'tree') {
    const label = task?.type === 'daily' ? task.label : 'Tree';
    const leaves = p.leaves === 'sparse' ? (p.streak > 0 ? ` · leaves filling in (${p.streak}/${app.store.rules.leafRecovery})` : ' · leaves resting') : '';
    const name = treeName(p.species, p.biome);
    return `${label}: ${p.stage === 'seed' ? `${name} seed` : `${p.stage} ${name}`}${leaves}`;
  }
  if (p.kind === 'wildflower') return p.bloomed ? task?.text ?? 'Wildflower' : `${task?.text ?? 'Bud'} (still a bud)`;
  if (p.kind === 'flower') return `A ${SEED_NAMES[p.species as keyof typeof SEED_NAMES] ?? 'flower'}`;
  return 'A plant';
}

/** How a toast names a tree: its label, or its kind. */
function treeLabel(s: GameState, p: Plant): string {
  const task = s.tasks.find((t) => t.id === p.taskId);
  const label = task?.type === 'daily' ? task.label : '';
  return label ? `the ${label} tree` : `the ${treeName(p.species, p.biome)}`;
}

/* ---------- dev mode: Newt places the suggested tree spots ---------- */
const spotEdit = { on: false };
const SPOT_KEY = 'thicket.dev.spots';
let devSpots: Partial<Record<Biome, [number, number][]>> | null = null;

/** Dev mode tries out spots saved on this device; they reach everyone once they're baked into core. */
function useDevSpots() {
  if (devSpots) return;
  try {
    devSpots = JSON.parse(localStorage.getItem(SPOT_KEY) ?? '{}') ?? {};
  } catch {
    devSpots = {};
  }
  for (const b of BIOMES) if (devSpots![b]) setSuggestedSpots(b, devSpots![b]!);
}
function saveDevSpots() {
  try {
    localStorage.setItem(SPOT_KEY, JSON.stringify(devSpots ?? {}));
  } catch {
    // Still in use until the page reloads.
  }
}
export function setSpotEditing(on: boolean) {
  update(() => {
    spotEdit.on = on;
    if (on) {
      app.planting = null;
      editor.on = false;
      drawerOpen = false;
    }
  });
}
function toggleSpot(biome: Biome, { c, r }: { c: number; r: number }) {
  useDevSpots();
  const now = suggestedSpots(biome);
  const has = now.some(([sc, sr]) => sc === c && sr === r);
  if (!has && !isPlantable(art.map(biome), c, r)) return toast('Spots go on open ground');
  const next: [number, number][] = has ? now.filter(([sc, sr]) => sc !== c || sr !== r) : [...now, [c, r]];
  devSpots![biome] = next;
  setSuggestedSpots(biome, next);
  saveDevSpots();
  update();
}
function resetSpots(biome: Biome) {
  useDevSpots();
  delete devSpots![biome];
  setSuggestedSpots(biome, null);
  saveDevSpots();
  update();
}
async function copySpots() {
  useDevSpots();
  const out = Object.fromEntries(BIOMES.filter((b) => devSpots![b]).map((b) => [b, devSpots![b]]));
  const text = `Bake these suggested spots into Thicket: ${JSON.stringify(out)}`;
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied. Paste it to Claude to bake these spots in for everyone', 2600);
  } catch {
    prompt('Copy this and paste it to Claude:', text);
  }
}
function spotToolbar(s: GameState) {
  const biome = currentBiome(s);
  const n = suggestedSpots(biome).length;
  const mine = !!devSpots?.[biome];
  const edited = BIOMES.some((b) => devSpots?.[b]);
  return html`<div class="box scene-edit" role="toolbar" aria-label="Suggested spots">
    <div class="scene-edit-row">
      <span class="chip" aria-hidden="true">Suggested spots</span>
      <button class="chip primary" @click=${() => setSpotEditing(false)}>Done</button>
    </div>
    <p class="small" style="margin:0">${n} spot${n === 1 ? '' : 's'} in the ${BIOME_NAMES[biome].toLowerCase()}${mine ? ', placed on this device' : handPlacedSpots(biome) ? ', placed by hand' : ', spread out automatically'}. Tap open ground to add one, or a glowing spot to take it away.</p>
    <div class="scene-edit-row">
      <button class="chip" ?disabled=${!mine} @click=${() => resetSpots(biome)}>Undo my changes here</button>
      <button class="chip" ?disabled=${!edited} @click=${copySpots}>Copy for Claude</button>
    </div>
  </div>`;
}

/* ---------- scenery editor: move the Lab's fixed pieces, or put them away in the bank ---------- */
const editor = { on: false, sel: null as (Piece & { w: number; h: number; ax: number; ay: number }) | null, bankOpen: false, toolbarOpen: true };
let pieceDrag: { id: string; x: number; y: number; px: number; py: number; moved: boolean } | null = null;

const editsOf = (s: GameState, biome: Biome) => s.profile.scenes?.[biome] ?? { moves: {}, hidden: [] };
function saveEdits(biome: Biome, moves: Record<string, { x: number; y: number }>, hidden: string[]) {
  return app.store.act({ type: 'editScene', biome, moves, hidden });
}
export function setEditing(on: boolean) {
  update(() => {
    editor.on = on;
    editor.sel = null;
    editor.bankOpen = false;
    if (on) app.planting = null;
  });
}
function editDown(e: PointerEvent): boolean {
  if (!editor.on || !canvas) return false;
  const p = toLogical(e);
  const hit = scenery.pieceAt(p.x, p.y);
  editor.sel = hit;
  if (!hit) {
    update();
    return false;
  }
  pieceDrag = { id: hit.id, x: hit.x, y: hit.y, px: p.x, py: p.y, moved: false };
  (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  update();
  return true;
}
function editMove(e: PointerEvent) {
  if (!pieceDrag || !canvas) return;
  const p = toLogical(e);
  const dx = p.x - pieceDrag.px, dy = p.y - pieceDrag.py;
  if (Math.abs(dx) + Math.abs(dy) > 1) pieceDrag.moved = true;
  scenery.move(pieceDrag.id, pieceDrag.x + dx, pieceDrag.y + dy);
}
function editUp(e: PointerEvent) {
  const d = pieceDrag;
  if (!d) return;
  pieceDrag = null;
  const s = app.store.view;
  if (!d.moved || !s || !canvas) return;
  dragged = true; // the click that ends a drag isn't a tap
  const p = toLogical(e);
  const biome = currentBiome(s);
  const x = Math.round(Math.max(0, Math.min(W, d.x + p.x - d.px)));
  const y = Math.round(Math.max(0, Math.min(H, d.y + p.y - d.py)));
  const cur = editsOf(s, biome);
  saveEdits(biome, { ...cur.moves, [d.id]: { x, y } }, cur.hidden);
  if (editor.sel) editor.sel = { ...editor.sel, x, y };
}
function putAway() {
  const s = app.store.view;
  const sel = editor.sel;
  if (!s || !sel) return;
  const biome = currentBiome(s);
  const cur = editsOf(s, biome);
  const { [sel.id]: _gone, ...moves } = cur.moves;
  void _gone;
  if (saveEdits(biome, moves, [...cur.hidden.filter((h) => h !== sel.id), sel.id])) {
    toast(`${sel.name} put in the bank`);
    update(() => (editor.sel = null));
  }
}
function putBack(id: string) {
  const s = app.store.view;
  if (!s) return;
  const biome = currentBiome(s);
  const cur = editsOf(s, biome);
  saveEdits(biome, cur.moves, cur.hidden.filter((h) => h !== id));
}
function resetScene() {
  const s = app.store.view;
  if (!s) return;
  saveEdits(currentBiome(s), {}, []);
  update(() => (editor.sel = null));
}

function editToolbar(s: GameState) {
  const biome = currentBiome(s);
  const cur = editsOf(s, biome);
  const bank = editor.bankOpen ? scenery.bank(biome) : [];
  const changed = Object.keys(cur.moves).length + cur.hidden.length;
  return html`<div class="box scene-edit" role="toolbar" aria-label="Scenery editor">
    <div class="scene-edit-row">
      <button class="chip" @click=${() => update(() => (editor.toolbarOpen = !editor.toolbarOpen))} aria-expanded=${editor.toolbarOpen}>${editor.toolbarOpen ? '▾' : '▸'} Editing scenery</button>
      <button class="chip primary" @click=${() => setEditing(false)}>Done</button>
    </div>
    ${editor.toolbarOpen ? html`
      <p class="small" style="margin:0">${editor.sel ? `${editor.sel.name} selected. Drag it, or put it away.` : 'Tap a log, stump, rock or bush, then drag it.'}</p>
      <div class="scene-edit-row">
        <button class="chip" ?disabled=${!editor.sel} @click=${putAway}>Put away</button>
        <button class="chip" aria-pressed=${editor.bankOpen} @click=${() => update(() => (editor.bankOpen = !editor.bankOpen))}>Bank (${cur.hidden.length})</button>
        <button class="chip" ?disabled=${!changed} @click=${resetScene}>Reset scene</button>
      </div>
      ${editor.bankOpen ? html`<div class="bank">${bank.length ? bank.map((b) => html`<button class="bank-item" @click=${() => putBack(b.id)} title="Put back">
          <img src=${b.png} alt="" style="image-rendering:pixelated"><span class="small">${b.name}</span></button>`)
        : html`<p class="small muted" style="margin:0">Nothing put away yet. Pieces you put away wait here; tap one to put it back where it was.</p>`}</div>` : nothing}
    ` : nothing}
  </div>`;
}

function tap(e: MouseEvent) {
  if (dragged) {
    dragged = false;
    return;
  }
  if (editor.on) return;
  const s = app.store.view;
  if (!s || !canvas) return;
  // No art yet, so nothing to tap on (and no ground to plant in).
  if (art.name === 'lab' && !labReady()) return;
  const { x, y } = toLogical(e);
  const biome = currentBiome(s);
  if (spotEdit.on) {
    toggleSpot(biome, tileAt(x, y));
    return;
  }
  const planting = app.planting;
  if (planting) {
    const { c, r } = tileAt(x, y);
    const what = plantingWhat(s) ?? undefined;
    const problem = plantProblem(s, biome, c, r, what);
    if (problem) {
      refused = { c, r, problem, until: tick + 20 };
      if (problem === 'crowded') {
        const near = crowdedBy(s, biome, c, r, what!)[0];
        const label = near ? treeLabel(s, near) : 'another tree';
        toast(`Too close to ${label}. Trees need room to grow big`);
      } else toast(problem === 'taken' ? 'Something is already growing there' : 'Pick an open patch of ground');
      return;
    }
    refused = null;
    const spot = spotFor(biome, c, r);
    const ok = planting.kind === 'plant'
      ? app.store.act({ type: 'placePlant', plantId: planting.plantId, spot })
      : app.store.act({ type: 'plantSeed', seed: planting.seed, spot });
    if (ok) {
      if (planting.kind === 'plant') toast(`Planted ${planting.label}`);
      update(() => {
        // Keep planting seeds while there are some left.
        if (planting.kind === 'seed' && seedCount(app.store.view!, planting.seed) > 0) return;
        app.planting = null;
      });
    }
    return;
  }
  const hit = hitTest(s, biome, x, y);
  if (hit.bed) {
    update(() => (drawerOpen = true));
    requestAnimationFrame(() => document.getElementById('bed')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }));
    return;
  }
  if (hit.plant && app.dev) {
    update(() => {
      app.devPick = hit.plant!.id;
      drawerOpen = true;
    });
    return;
  }
  if (hit.plant) {
    const p = hit.plant;
    // A bloom offers picking; anything that belongs to a task opens the task; the rest just says what it is.
    if (p.kind === 'wildflower' && p.bloomed) update(() => (app.sheet = { kind: 'bloom', plantId: p.id }));
    else if (p.taskId && s.tasks.some((t) => t.id === p.taskId)) {
      toast(describe(s, p), 1800);
      update(() => (app.sheet = { kind: 'task', taskId: p.taskId }));
    } else toast(describe(s, p));
    return;
  }
  if (hit.animal) {
    const a = s.inventory.find((i) => i.id === hit.animal)!;
    toast(`${a.name} the ${ANIMAL_NAMES[a.kind as AnimalKind]?.toLowerCase() ?? a.kind}`);
  }
}

function plantIcon(s: GameState, p: Plant) {
  const biome = currentBiome(s);
  return icon(`${p.kind}:${p.species}:${p.pace}:${p.bloomed}:${biome}`, (ctx) =>
    p.kind === 'tree'
      ? art.tree(ctx, { x: 12, y: 22, species: p.species, stage: 'small', sparse: false, biome, seed: 1, tick: 0, reduce: true })
      : p.bloomed
        ? art.bloom(ctx, { x: 12, y: 22, pace: p.pace, daysLeft: 7, biome, tick: 0, reduce: true })
        : art.bud(ctx, { x: 12, y: 22, pace: p.pace, drooping: false, biome }));
}

function tray(s: GameState) {
  const live = new Set(s.tasks.filter((t) => !t.archivedAt).map((t) => t.id));
  const waiting = s.plants.filter((p) => !p.goneAt && p.taskId && live.has(p.taskId) && p.biome === null &&
    (p.kind === 'tree' || p.kind === 'wildflower'));
  if (!waiting.length) return nothing;
  const trees = waiting.filter((p) => p.kind === 'tree');
  const buds = waiting.filter((p) => p.kind === 'wildflower');
  const chip = (p: Plant) => {
    // An unlabeled tree goes by its daily's name.
    const label = p.kind === 'tree' ? (s.tasks.find((t) => t.id === p.taskId) as { label?: string })?.label || taskText(s, p.taskId) || 'tree' : taskText(s, p.taskId);
    const on = app.planting?.kind === 'plant' && app.planting.plantId === p.id;
    return html`<button class="chip" aria-pressed=${on} @click=${() => update(() => (app.planting = on ? null : { kind: 'plant', plantId: p.id, label: taskText(s, p.taskId) || label }))}>
      <img src=${plantIcon(s, p)} width="24" height="24" alt="">${label}</button>`;
  };
  return html`<section class="box">
    <div class="section-head"><h3>Ready to plant</h3><span class="muted small">Pick one, then tap open grass</span></div>
    ${trees.length ? html`<p class="small muted" style="margin:0 0 6px">Trees are planted once, so choose a spot you like.</p><div class="chips">${trees.map(chip)}</div>` : nothing}
    ${buds.length ? html`<div class="chips" style="margin-top:8px">${buds.map(chip)}</div>` : nothing}
  </section>`;
}

function bed(s: GameState) {
  const habits = s.tasks.filter((t) => t.type === 'habit' && !t.archivedAt);
  const plants = s.plants.filter((p) => p.kind === 'bed' && !p.goneAt && habits.some((h) => h.id === p.taskId));
  const unplaced = plants.filter((p) => p.plot === null);
  const plots = Array.from({ length: app.store.rules.bedPlots }, (_, i) => plants.find((p) => p.plot === i));
  const tapPlot = (i: number) => {
    const here = plots[i];
    if (bedPick) {
      if (app.store.act({ type: 'placePlant', plantId: bedPick, spot: { where: 'bed', plot: i } })) bedPick = null;
      update();
      return;
    }
    if (!here) return toast('An empty plot. Pick a habit plant above to grow it here.');
    if (app.dev) return update(() => (app.devPick = here.id));
    const weed = s.plants.find((p) => p.kind === 'weed' && p.taskId === here.taskId && !p.goneAt);
    if (weed) {
      if (app.store.act({ type: 'pullWeed', plantId: weed.id })) toast('Pulled a weed');
      return;
    }
    toast(`${taskText(s, here.taskId)}: ${here.stage} plant`);
  };
  return html`<section class="box" id="bed">
    <div class="section-head"><h3>Raised bed</h3><span class="muted small">Habits grow here</span></div>
    ${unplaced.length ? html`<div class="chips" style="margin-bottom:8px">${unplaced.map((p) => html`<button class="chip" aria-pressed=${bedPick === p.id}
      @click=${() => update(() => (bedPick = bedPick === p.id ? null : p.id))}>${taskText(s, p.taskId)}</button>`)}</div>
      <p class="small muted" style="margin:0 0 8px">${bedPick ? 'Now tap a plot.' : 'Pick a habit, then tap a plot.'}</p>` : nothing}
    <div class="bed">${plots.map((p, i) => {
      const weeds = p ? s.plants.filter((w) => w.kind === 'weed' && w.taskId === p.taskId && !w.goneAt).length : 0;
      return html`<button class="plot" aria-label=${p ? `${taskText(s, p.taskId)}, ${p.stage}${weeds ? `, ${weeds} weeds: tap to pull one` : ''}` : `Empty plot ${i + 1}`}
        @click=${() => tapPlot(i)}>${p ? html`<img src=${icon(`bed:${p.stage}:${p.pace}:${hashOf(p.id) % 3}`, (ctx) => art.bedPlant(ctx, { x: 6, y: 13, stage: p.stage, seed: hashOf(p.id), pace: p.pace }), 13, 14)} alt="" style="width:80%;image-rendering:pixelated">` : nothing}
        ${weeds ? html`<span class="weeds">${'ᵥ'.repeat(weeds)}</span>` : nothing}</button>`;
    })}</div>
  </section>`;
}

function shop(s: GameState) {
  const rules = app.store.rules;
  const kinds = Object.keys(rules.animals) as AnimalKind[];
  const biome = currentBiome(s);
  const trees = s.plants.filter((p) => p.kind === 'tree' && !p.goneAt && p.biome === biome);
  return html`<section class="box" id="shop">
    <div class="section-head"><h3>Companions</h3><span class="muted small"><span class="coin"></span> ${s.profile.coins}</span></div>
    <div class="shop">${kinds.map((k) => {
      const rule = rules.animals[k];
      const needsTree = rule.minTreeStage !== null;
      const roomy = !needsTree || trees.some((t) => stageRank(t.stage) >= stageRank(rule.minTreeStage!) &&
        !s.inventory.some((i) => i.type === 'animal' && i.kind === k && i.homePlantId === t.id));
      const poor = s.profile.coins < rule.price;
      const note = needsTree && !roomy ? `Needs a ${rule.minTreeStage} tree here with room` : ANIMAL_NOTES[k];
      return html`<button class="item" ?disabled=${poor || !roomy}
        @click=${() => app.store.act(needsTree ? { type: 'buyAnimal', kind: k } : { type: 'buyAnimal', kind: k, biome })}>
        <span class="item-head"><img src=${icon(`animal:${k}`, (ctx) => art.animal(ctx, { x: 7, y: 13, kind: k, flip: false, moving: false, tick: 0, reduce: true }), 14, 14)} width="42" height="42" alt="">
        <span><span class="name">${ANIMAL_NAMES[k]}</span><br><span class="small"><span class="coin"></span> ${rule.price}</span></span></span>
        <span class="small muted">${note}</span></button>`;
    })}</div>
  </section>`;
}

function seeds(s: GameState) {
  const have = SEED_KINDS.filter((k) => seedCount(s, k) > 0);
  return html`<section class="box">
    <div class="section-head"><h3>Seed packets</h3><span class="muted small">Decorative flowers</span></div>
    ${have.length ? html`<div class="chips">${have.map((k) => {
      const on = app.planting?.kind === 'seed' && app.planting.seed === k;
      return html`<button class="chip" aria-pressed=${on} @click=${() => update(() => (app.planting = on ? null : { kind: 'seed', seed: k }))}>
        <img src=${icon(`flower:${k}`, (ctx) => art.flower(ctx, { x: 12, y: 22, seed: k, biome: currentBiome(s) }))} width="24" height="24" alt="">${SEED_NAMES[k]} ×${seedCount(s, k)}</button>`;
    })}</div>` : html`<p class="empty" style="margin:0">Finishing tasks sometimes drops a seed packet, and wildflowers leave one when they go to seed.</p>`}
  </section>`;
}

/**
 * Full screen: the scene takes the whole display (no header, tabs or drawer).
 * Uses the browser's full screen where it has one (computers, Android); on an
 * iPhone the page just hides everything but the garden.
 */
function toggleImmersive() {
  setImmersive(!app.immersive);
}

/** Turns full screen on or off, keeping the page and the browser's full screen in step. */
export function setImmersive(on: boolean) {
  update(() => {
    app.immersive = on;
    app.planting = null;
    // Full screen means filling it: no black bars from "Whole garden".
    if (on && zoom === 'fit') {
      zoom = 'close';
      extra = 1;
      centred = false;
    }
  });
  try {
    const root = document.documentElement;
    if (on && root.requestFullscreen && !document.fullscreenElement) void root.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
    if (!on && document.fullscreenElement) void document.exitFullscreen().catch(() => {});
  } catch {
    // The page's own full screen still works.
  }
}

if (typeof document !== 'undefined') {
  // The browser's full screen leads: entering it (from the button or the browser) hides the
  // page's bars too, and leaving it (Esc, the back gesture) brings them back.
  document.addEventListener('fullscreenchange', () => {
    const fs = !!document.fullscreenElement;
    if (fs !== app.immersive && app.tab === 'garden') setImmersive(fs);
  });
}

/** The planting banner's hint: glowing spots for a tree, or that there's no room left for one. */
function plantingHint(s: GameState, biome: Biome): string {
  const what = plantingWhat(s);
  if (what?.kind !== 'tree') return 'tap open ground';
  if (!findOpenSpot(s, biome, 'anywhere', '', what)) return 'no room left here for another tree to grow';
  return glowSpots(s, biome, what).length ? 'tap a glowing spot, or any open ground with room' : 'tap open ground with room to grow';
}

/** What the drawer's handle says while it's closed. */
function summary(s: GameState) {
  const live = new Set(s.tasks.filter((t) => !t.archivedAt).map((t) => t.id));
  const ready = s.plants.filter((p) => !p.goneAt && p.taskId && live.has(p.taskId) && p.biome === null && (p.kind === 'tree' || p.kind === 'wildflower')).length;
  return ready ? `${ready} ready to plant` : 'Bed, seeds and companions';
}

/** Over the garden while the Lab art is on its way, or if it couldn't load (offline before it was saved). */
function artPending() {
  if (artState() !== 'unavailable') return html`<p class="scene-loading" role="status">Growing your garden…</p>`;
  const retry = () => {
    void startLab().catch(() => {});
    update();
  };
  return html`<div class="scene-note" role="status">
    <p>The garden art couldn't load.</p>
    <p class="hint">Your garden is safe. It will show once the art arrives.</p>
    <button class="primary" @click=${retry}>Try again</button>
  </div>`;
}

export function gardenView(s: GameState) {
  const biome = currentBiome(s);
  const biomes = s.unlocks.map((u) => u.biome);
  const planting = app.planting;
  // While planting, the drawer gets out of the way so the ground is visible.
  const open = drawerOpen && !planting;
  const artMissing = art.name === 'lab' && !labReady();
  return html`<div class=${`garden ${open ? 'drawer-open' : ''}`}>
    <div class="garden-stage">
      ${artMissing ? html`<div class="art-backdrop" aria-hidden="true"></div>` : nothing}
      <div class=${`scene-frame ${zoom}`}
        @pointerdown=${(e: PointerEvent) => { if (editDown(e)) return; touchStart(e); panStart(e); }} @pointermove=${(e: PointerEvent) => { if (pieceDrag) { editMove(e); return; } touchMove(e); panMove(e); }}
        @pointerup=${(e: PointerEvent) => { editUp(e); touchEnd(e); panEnd(); }} @pointercancel=${(e: PointerEvent) => { editUp(e); touchEnd(e); panEnd(); }} @pointerleave=${panEnd}
        @wheel=${{ handleEvent: wheel, passive: false }}>
        <canvas width=${W} height=${H} ${ref(attach)} aria-label=${`Your ${BIOME_NAMES[biome].toLowerCase()}. Tap a plant to see it.`}
          @click=${tap} @pointermove=${(e: PointerEvent) => { if (!app.planting && !spotEdit.on) return; if (e.pointerType !== 'mouse') { hover = null; return; } const p = toLogical(e); hover = tileAt(p.x, p.y); }}
          @pointerleave=${() => (hover = null)}></canvas>
      </div>
      ${artMissing ? artPending() : nothing}
      <div class="scene-top">
        ${biomes.length > 1 ? html`<div class="chips biome-chips">${biomes.map((b) => html`<button class="chip" aria-pressed=${b === biome}
          @click=${() => update(() => { app.biome = b; app.planting = null; centred = false; })}>${BIOME_NAMES[b]}</button>`)}</div>` : html`<span></span>`}
        <div class="chips scene-tools">
          <button class="chip" @click=${() => setZoom(zoom === 'close' && extra === 1 ? 'fit' : 'close')}>${zoom === 'close' && extra === 1 ? 'Whole garden' : 'Fill screen'}</button>
          <button class="chip" aria-pressed=${app.immersive} @click=${toggleImmersive}
            aria-label=${app.immersive ? 'Leave full screen' : 'Full screen'}>${app.immersive ? 'Exit full screen' : 'Full screen'}</button>
          ${!editor.on && !spotEdit.on && labReady() ? html`<button class="chip" @click=${() => setEditing(true)}>Edit scenery</button>` : nothing}
          ${app.dev && !editor.on && !spotEdit.on && labReady() ? html`<button class="chip" @click=${() => setSpotEditing(true)}>Suggested spots</button>` : nothing}
        </div>
      </div>
      ${editor.on ? editToolbar(s) : nothing}
      ${spotEdit.on ? spotToolbar(s) : nothing}
      ${planting ? html`<div class="box planting"><span>${planting.kind === 'plant' ? `Planting ${planting.label}` : `Planting a ${SEED_NAMES[planting.seed]}`} · ${plantingHint(s, biome)}</span>
        <button @click=${() => update(() => (app.planting = null))}>Stop</button></div>` : nothing}
    </div>
    <aside class="garden-side" aria-label="Garden tools">
      <button class="drawer-handle" aria-expanded=${open} @click=${() => update(() => { drawerOpen = !open; if (planting) app.planting = null; })}>
        <span class="grip" aria-hidden="true"></span><span>${open ? 'Close' : summary(s)}</span><span aria-hidden="true">${open ? '▼' : '▲'}</span></button>
      <div class="drawer-body">
        ${devPanel(s)}
        ${tray(s)}
        ${bed(s)}
        ${seeds(s)}
        ${shop(s)}
      </div>
    </aside>
  </div>`;
}

export function bloomSheet(s: GameState, plantId: string) {
  const p = s.plants.find((x) => x.id === plantId && !x.goneAt && x.bloomed);
  const close = () => update(() => (app.sheet = null));
  if (!p) return nothing;
  const coins = app.store.rules.pickCoins[p.pace];
  const pick = (use: 'coins' | 'bouquet' | 'press') => {
    if (app.store.act({ type: 'pickBloom', plantId, use })) close();
  };
  return html`<div class="scrim" @click=${(e: Event) => e.target === e.currentTarget && close()}>
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Wildflower">
      <div class="sheet-head"><h2>${taskText(s, p.taskId) || 'Wildflower'}</h2><button @click=${close}>Close</button></div>
      <p style="margin:0">In full bloom for ${p.bloomDaysLeft} more day${p.bloomDaysLeft === 1 ? '' : 's'}. If you leave it, it goes to seed and leaves you a seed packet.</p>
      <div class="actions">
        <button class="primary" @click=${() => pick('coins')}>Sell for ${coins} coins</button>
        <button @click=${() => pick('bouquet')}>Add to bouquet</button>
        <button @click=${() => pick('press')}>Press in the book</button>
      </div>
      <button class="link" style="align-self:flex-start" @click=${() => { if (p.taskId && app.store.act({ type: 'scoreTask', id: p.taskId, direction: 'down' })) close(); }}>
        Not done after all? Undo it</button>
    </div>
  </div>`;
}
